import { useState } from 'react';
import { AlertTriangle, ChevronLeft, ChevronRight, ExternalLink, X } from 'lucide-react';
import { waUrl } from './shared';

const OUTCOMES = [['sent', 'Sent', 'bg-teal-600 hover:bg-teal-700'], ['skipped', 'Skip', 'bg-slate-500 hover:bg-slate-600'], ['failed', 'Failed', 'bg-rose-600 hover:bg-rose-700']];

export const OutreachQueue = ({ queue, setQueue, api, onClose, onRecorded }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const { items, index, results } = queue;
  const item = items[index];
  const done = Object.keys(results).length;
  const tally = Object.values(results).reduce((a, r) => ({ ...a, [r]: (a[r] || 0) + 1 }), {});

  const go = (i) => setQueue({ ...queue, index: Math.max(0, Math.min(items.length - 1, i)) });
  const edit = (message) => setQueue({ ...queue, items: items.map((x, i) => (i === index ? { ...x, message } : x)) });

  const mark = async (outcome) => {
    if (results[item.id] && !window.confirm(`Already marked "${results[item.id]}". Record "${outcome}" as well?`)) return;
    setBusy(true); setError('');
    const ok = await api(`/students/${item.id}/contact`, { method: 'POST', body: JSON.stringify({ outcome, template_key: item.template_key, message: item.message }) });
    setBusy(false);
    if (!ok) { setError('Could not save. Please try again.'); return; }
    const next = { ...queue, results: { ...results, [item.id]: outcome } };
    const remaining = items.findIndex((x, i) => i > index && !next.results[x.id]);
    setQueue({ ...next, index: remaining === -1 ? index : remaining });
    onRecorded();
  };

  const finished = done === items.length;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4" data-testid="outreach-queue">
      <div className="absolute inset-0 bg-slate-900/40" />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-xl max-h-[92vh] overflow-y-auto p-6 space-y-4">
        <button type="button" onClick={onClose} className="absolute right-4 top-4 text-slate-400 hover:text-slate-700" title="Close (progress is saved)" data-testid="outreach-queue-close"><X className="w-5 h-5" /></button>
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-wider text-teal-700">Guided WhatsApp sending</p>
          <p className="text-sm text-slate-600 mt-1" data-testid="outreach-queue-progress">Student {index + 1} of {items.length} · ✓ {tally.sent || 0} sent · {tally.skipped || 0} skipped · {tally.failed || 0} failed · {items.length - done} left</p>
          <div className="h-1.5 bg-slate-100 rounded-full mt-2"><div className="h-full bg-teal-500 rounded-full transition-[width]" style={{ width: `${(done / items.length) * 100}%` }} /></div>
        </div>
        {finished && <div className="rounded-xl bg-teal-50 border border-teal-100 p-4 text-sm text-teal-900" data-testid="outreach-queue-finished">All {items.length} students processed. You can close this queue.</div>}
        <div className="rounded-xl border border-slate-200 p-4 space-y-3">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-slate-900" data-testid="outreach-queue-student">{item.full_name}</p>
              <p className="text-xs text-slate-500">+{item.phone_e164} · {item.template_name}{!item.whatsapp_consent && ' · no WhatsApp opt-in recorded'}</p>
            </div>
            {results[item.id] && <span className="text-xs font-semibold rounded-full bg-slate-100 px-2.5 py-1" data-testid="outreach-queue-item-result">{results[item.id]}</span>}
          </div>
          {item.repeat_warning && <p className="text-xs text-amber-800 bg-amber-50 rounded-lg px-3 py-2 flex items-center gap-1.5" data-testid="outreach-repeat-warning"><AlertTriangle className="w-3.5 h-3.5" />{item.repeat_warning}. Check before messaging again.</p>}
          <textarea value={item.message} onChange={(e) => edit(e.target.value)} rows={8} className="w-full rounded-lg border border-slate-200 p-3 text-sm bg-[#f3fbf4] focus:outline-none focus:ring-2 focus:ring-teal-400" data-testid="outreach-queue-message" />
          <a href={waUrl(item.phone_e164, item.message)} target="_blank" rel="noopener noreferrer" data-testid="outreach-open-whatsapp-btn"
            className="w-full inline-flex justify-center items-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe5b] text-white font-semibold py-2.5">
            Open WhatsApp <ExternalLink className="w-4 h-4" />
          </a>
          <p className="text-xs text-slate-500 text-center">After sending (or not) in WhatsApp, come back and record what happened:</p>
          <div className="grid grid-cols-3 gap-2">
            {OUTCOMES.map(([o, label, cls]) => (
              <button key={o} type="button" disabled={busy} onClick={() => mark(o)} data-testid={`outreach-mark-${o}-btn`} className={`rounded-full text-white text-sm font-semibold py-2 disabled:opacity-50 ${cls}`}>{label}</button>
            ))}
          </div>
          {error && <p className="text-xs text-rose-600" data-testid="outreach-queue-error">{error}</p>}
        </div>
        <div className="flex justify-between">
          <button type="button" onClick={() => go(index - 1)} disabled={index === 0} className="inline-flex items-center gap-1 text-sm text-slate-600 disabled:opacity-30" data-testid="outreach-prev-btn"><ChevronLeft className="w-4 h-4" />Previous</button>
          <button type="button" onClick={() => go(index + 1)} disabled={index === items.length - 1} className="inline-flex items-center gap-1 text-sm text-slate-600 disabled:opacity-30" data-testid="outreach-next-btn">Next<ChevronRight className="w-4 h-4" /></button>
        </div>
      </div>
    </div>
  );
};
